import React from 'react';
import MuiSlider from '@mui/material/Slider';
import type { SliderProps } from '@mui/material/Slider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';

type SliderCustomProps = SliderProps & {
    hideThumbButtons?: boolean;
    leftText?: string;
    rightText?: string;
    isEditable?: boolean;
    isEditingDisabled?: boolean;
    onEditIconClick?: () => void;
};

const IMPORTANCE_LEVELS = [
    'Doesn\'t matter',
    'Slightly important',
    'Moderately important',
    'Very important',
    'Essential',
] as const;

const getImportanceLabel = (value: number): string => {
    if (value <= 20) {
        return IMPORTANCE_LEVELS[0];
    }
    if (value <= 40) {
        return IMPORTANCE_LEVELS[1];
    }
    if (value <= 60) {
        return IMPORTANCE_LEVELS[2];
    }
    if (value <= 80) {
        return IMPORTANCE_LEVELS[3];
    }

    return IMPORTANCE_LEVELS[4];
};

const SliderV2 = ({
    hideThumbButtons = false,
    leftText = '',
    rightText = '',
    isEditable = false,
    onEditIconClick,
    value,
    defaultValue = 50,
    onChange,
    isEditingDisabled = false,
    ...props
}: SliderCustomProps) => {
    const [ internalValue, setInternalValue ] = React.useState<number>(
        typeof defaultValue === 'number' ? defaultValue : 50,
    );

    const currentValue =
        typeof value === 'number' ? value : internalValue;

    const handleChange: SliderProps['onChange'] = (event, newValue, activeThumb) => {
        if (typeof newValue === 'number') {
            if (value === undefined) {
                setInternalValue(newValue);
            }
        }

        onChange?.(event, newValue, activeThumb);
    };

    const showHelpTexts = Boolean(leftText.trim() && rightText.trim());

    return (
        <Box width="100%">
            {/* Current importance */}
            {!hideThumbButtons && (
                <Box
                    display="flex"
                    justifyContent="flex-end"
                    mb={0.5}
                >
                    <Typography
                        variant="caption"
                        fontWeight={600}
                        color="primary.main"
                    >
                        {getImportanceLabel(currentValue)}
                    </Typography>
                </Box>
            )}

            {/* Slider */}
            <Box display="flex" alignItems="center" gap={1}>
                <MuiSlider
                    min={0}
                    max={100}
                    step={1}
                    aria-label="Importance"
                    valueLabelDisplay="off"
                    sx={{
                        color: 'var(--app-primary, #2878d4)',
                        height: 6,

                        '& .MuiSlider-thumb': {
                            width: 18,
                            height: 18,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                        },

                        '& .MuiSlider-track': {
                            border: 'none',
                        },

                        '& .MuiSlider-rail': {
                            opacity: 0.3,
                        },
                    }}
                    {...props}
                    value={value}
                    defaultValue={value === undefined ? defaultValue : undefined}
                    onChange={handleChange}
                />

                {isEditable && (
                    <IconButton
                        size="small"
                        onClick={onEditIconClick}
                        aria-label="Edit criterion"
                    >
                        <EditIcon
                            fontSize="small"
                            sx={{ color: 'var(--app-primary, #2878d4)' }}
                        />
                    </IconButton>
                )}
            </Box>

            {/* Importance scale */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
            >
                <Typography variant="caption" color="text.secondary">
                    {showHelpTexts ? leftText : 'Not important'}
                </Typography>

                <Typography variant="caption" color="text.secondary">
                    {showHelpTexts ? rightText : 'Essential'}
                </Typography>
            </Box>
        </Box>
    );
};

export default SliderV2;
